import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Stethoscope, Heart, Activity, Microscope, Pill, Dumbbell, Hand, UserCheck, LogIn, Eye, ClipboardList } from "lucide-react";
import { LegalFooter } from "@/components/layout/legal-footer";
import { useAuth, useUser } from "@clerk/react";
import { useApprovalStatus } from "@/hooks/use-approval-status";

type Role = "nurse" | "patient" | "doctor" | "pharmacist" | "physio" | "ot" | "coordinator";

export default function ChooseRole() {
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState<Role | null>(null);
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user } = useUser();
  const { data: profileCheck } = useApprovalStatus(isSignedIn === true);
  const isAdmin = Boolean(profileCheck?.isAdmin);
  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");

  // Captured once on mount from the URL, before the cleanup effect runs.
  // Using a state initializer ensures the value is stable even after Clerk
  // hydrates asynchronously and the URL param is stripped.
  const [wasJustCleared] = useState(
    () => new URLSearchParams(window.location.search).get("recovered") === "1",
  );

  // Strip the query param after mount so it doesn't persist across navigation.
  useEffect(() => {
    if (wasJustCleared) {
      history.replaceState(null, "", "/choose-role");
    }
  }, [wasJustCleared]);

  const handleRoleSelection = async (role: Role) => {
    setLoading(role);
    localStorage.setItem("rheumhub_role", role);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/user/role`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role, email: user?.primaryEmailAddress?.emailAddress }),
      });
      if (res.ok) {
        const profile = await res.json() as { approvalStatus?: string };
        // Admins bypass the approval gate — they may inspect any portal
        // regardless of their profile's approvalStatus. For everyone else,
        // new signups default to 'pending' and HCP role changes force
        // 'pending', so send them to the approval gate immediately.
        if (!isAdmin) {
          if (profile.approvalStatus === "rejected") {
            setLocation("/pending-approval?rejected=1");
            return;
          }
          if (profile.approvalStatus !== "approved") {
            setLocation("/pending-approval");
            return;
          }
        }
      }
    } catch {
      // Best-effort — localStorage role still used for routing
    }
    // Admins skip onboarding too — straight to the chosen portal.
    if (role === "patient" || isAdmin) {
      setLocation(`/${role}`);
    } else {
      setLocation("/onboarding");
    }
  };

  const handleBrowseAsGuest = () => {
    localStorage.setItem("rheumhub_role", "guest");
    if (isAdvicelineHub) {
      setLocation("/patient");
    } else {
      setLocation("/clinical-tools");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Recovery prompt — only rendered when browser-storage-card redirected here
            with ?recovered=1, signalling the user just cleared their browser data.
            Two variants based on current Clerk session state. */}
        {wasJustCleared && authLoaded && (
          <div className="w-full max-w-4xl mb-6">
            {isSignedIn ? (
              /* Clerk session still alive (only localStorage was cleared) */
              <div className="bg-teal-50 border border-teal-200 rounded-2xl px-6 py-4 flex items-start gap-3">
                <UserCheck className="h-5 w-5 text-teal-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-teal-900">
                    Welcome back{user?.firstName ? `, ${user.firstName}` : ""}!
                  </p>
                  <p className="text-sm text-teal-800 mt-0.5">
                    Your browser data has been cleared. You're still signed in to your RheumHub account — pick your role below to continue.
                  </p>
                </div>
              </div>
            ) : (
              /* Clerk session gone (cookies also cleared) — show sign-in CTA */
              <div className="bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 flex items-start gap-3">
                <LogIn className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Your browser data has been cleared.</p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Already have a RheumHub account?{" "}
                    <a
                      href="/sign-in"
                      className="text-teal-700 font-medium hover:underline"
                    >
                      Sign back in
                    </a>{" "}
                    to restore your preferences.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-10 text-center">
          <div className="flex justify-center mb-4">
            <Activity className="h-10 w-10 text-teal-700" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Welcome to RheumHub</h1>
          <p className="text-slate-600 mt-2">Select your role to get started</p>
        </div>

        <div className="w-full max-w-4xl space-y-8">
          {/* HCP Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">Healthcare Professionals</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => handleRoleSelection("nurse")}
                disabled={loading !== null}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-300 transition-all text-left flex flex-col h-full group focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-70"
                data-testid="button-role-nurse"
              >
                <div className="h-12 w-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
                  <Stethoscope className="h-6 w-6" />
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-1.5">Nurse</h2>
                <p className="text-slate-500 flex-1 text-sm leading-relaxed">
                  Clinical guidelines, medication reference, NMC scenarios, competency portfolio, and AI clinical assistant.
                </p>
                {loading === "nurse" && (
                  <p className="text-teal-600 text-sm mt-3 font-medium">Setting up your workspace…</p>
                )}
              </button>

              <button
                onClick={() => handleRoleSelection("doctor")}
                disabled={loading !== null}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all text-left flex flex-col h-full group focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-70"
                data-testid="button-role-doctor"
              >
                <div className="h-12 w-12 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                  <Microscope className="h-6 w-6" />
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-1.5">Doctor</h2>
                <p className="text-slate-500 flex-1 text-sm leading-relaxed">
                  Diagnostic criteria, disease activity scores, prescribing guidance, GP referral hub, and AI assistant.
                </p>
                {loading === "doctor" && (
                  <p className="text-indigo-600 text-sm mt-3 font-medium">Setting up your workspace…</p>
                )}
              </button>

              <button
                onClick={() => handleRoleSelection("pharmacist")}
                disabled={loading !== null}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-violet-300 transition-all text-left flex flex-col h-full group focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-70"
                data-testid="button-role-pharmacist"
              >
                <div className="h-12 w-12 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center mb-4 group-hover:bg-violet-100 transition-colors">
                  <Pill className="h-6 w-6" />
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-1.5">Pharmacist</h2>
                <p className="text-slate-500 flex-1 text-sm leading-relaxed">
                  DMARD monitoring, dose optimisation, biosimilar guidance, drug interactions, and AI medicines adviser.
                </p>
                {loading === "pharmacist" && (
                  <p className="text-violet-600 text-sm mt-3 font-medium">Setting up your workspace…</p>
                )}
              </button>

              <button
                onClick={() => handleRoleSelection("physio")}
                disabled={loading !== null}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all text-left flex flex-col h-full group focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-70"
                data-testid="button-role-physio"
              >
                <div className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                  <Dumbbell className="h-6 w-6" />
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-1.5">Physiotherapist</h2>
                <p className="text-slate-500 flex-1 text-sm leading-relaxed">
                  Exercise protocols, outcome measures, hydrotherapy, condition-specific protocols, and AI rehabilitation assistant.
                </p>
                {loading === "physio" && (
                  <p className="text-emerald-600 text-sm mt-3 font-medium">Setting up your workspace…</p>
                )}
              </button>

              <button
                onClick={() => handleRoleSelection("ot")}
                disabled={loading !== null}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all text-left flex flex-col h-full group focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-70"
                data-testid="button-role-ot"
              >
                <div className="h-12 w-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center mb-4 group-hover:bg-amber-100 transition-colors">
                  <Hand className="h-6 w-6" />
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-1.5">Occupational Therapist</h2>
                <p className="text-slate-500 flex-1 text-sm leading-relaxed">
                  Hand assessment, joint protection, fatigue management, ADL & functional independence, and OT AI assistant.
                </p>
                {loading === "ot" && (
                  <p className="text-amber-600 text-sm mt-3 font-medium">Setting up your workspace…</p>
                )}
              </button>
            </div>
          </div>

          {/* Practice operations (non-clinical) */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">Practice operations (non-clinical)</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <button
              onClick={() => handleRoleSelection("coordinator")}
              disabled={loading !== null}
              className="w-full bg-white p-6 rounded-2xl border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all text-left flex items-center gap-6 group focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-70"
              data-testid="button-role-coordinator"
            >
              <div className="h-12 w-12 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0 group-hover:bg-sky-100 transition-colors">
                <ClipboardList className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-slate-900 mb-0.5">Coordinator / Medical Secretary</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Appointment scheduling, advice line routing, patient roster, and team coordination. Clinical content (AI, prescribing, CPD) is not available.
                </p>
                {loading === "coordinator" && (
                  <p className="text-sky-600 text-sm mt-1 font-medium">Setting up your workspace…</p>
                )}
              </div>
            </button>
          </div>

          {/* Patient Section — only available on advicelinehub.co.uk */}
          {isAdvicelineHub && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">Living with a rheumatic condition?</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <button
              onClick={() => handleRoleSelection("patient")}
              disabled={loading !== null}
              className="w-full bg-rose-50 p-6 rounded-2xl border border-rose-100 hover:border-rose-300 hover:bg-rose-100/60 transition-all text-left flex items-center gap-6 group focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 disabled:opacity-70"
              data-testid="button-role-patient"
            >
              <div className="h-12 w-12 rounded-xl bg-white text-rose-500 flex items-center justify-center shrink-0 shadow-sm group-hover:shadow transition-shadow">
                <Heart className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-slate-900 mb-0.5">I'm a Patient</h2>
                <p className="text-slate-700 text-sm leading-relaxed">
                  Plain-language condition guides, medication information, trusted resources, and a supportive AI companion.
                </p>
                {loading === "patient" && (
                  <p className="text-rose-500 text-sm mt-1 font-medium">Setting up your workspace…</p>
                )}
              </div>
            </button>
          </div>
          )}

          {/* Guest option — only available on advicelinehub.co.uk */}
          {isAdvicelineHub && authLoaded && !isSignedIn && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">Not ready to sign up?</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <button
                onClick={handleBrowseAsGuest}
                disabled={loading !== null}
                className="w-full bg-white p-5 rounded-2xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left flex items-center gap-5 group focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-70"
                data-testid="button-browse-guest"
              >
                <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 group-hover:bg-slate-200 transition-colors">
                  <Eye className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-slate-700 mb-0.5">Explore without signing in</h2>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    Browse conditions, medications, and resources as a guest. An account is needed for the AI guide, symptom checker, and personalised features.
                  </p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
